import { useState } from 'react';

import { IconMinus, IconPlus } from '@iwoca/icons';
import { GetExampleRepaymentScheduleResponse } from '@iwoca/lapi-client/edge';
import { Button, Table } from '@iwoca/orion';
import { FormattedDate, useIntl } from 'react-intl';
import { connect } from 'react-redux';

import { FormattedCurrency } from '../../../iwIntlProvider/FormattedCurrency';
import {
    getRepaymentSchedule,
    getTotalInterestFromSchedule,
    getTotalPrincipleFromSchedule
} from '../repaymentScheduleSelectors';
import messages from './RepaymentsSchedule.messages';
import styles from './RepaymentsSchedule.module.css';
import { RootState } from '../../../iwProvider/configureStore';
import { formatUnfundedCapturedPaymentDates } from '../dashboard/Dashboard.helpers';

const mapStateToProps = (
    state: RootState,
    {
        account,
        schedule,
        totalInterest,
        totalPrinciple,
        totalPayable,
        overrideScheduleDates
    }: {
        account?: any;
        schedule?: GetExampleRepaymentScheduleResponse['data'];
        totalInterest?: number;
        totalPrinciple?: number;
        totalPayable?: number;
        overrideScheduleDates?: any;
    }
) => {
    if (account) {
        const repaymentSchedule = getRepaymentSchedule(state, account);
        const { selectedPlan } = state.iwocapayDashboard;

        return {
            totalPayable: repaymentSchedule.summary.total_payable,
            schedule:
                repaymentSchedule.schedule as GetExampleRepaymentScheduleResponse['data'],
            totalInterest: getTotalInterestFromSchedule(
                state,
                account
            ) as number,
            totalPrinciple: getTotalPrincipleFromSchedule(
                state,
                account
            ) as number,
            overrideScheduleDates:
                formatUnfundedCapturedPaymentDates(selectedPlan)
        };
    }

    return {
        schedule: schedule as GetExampleRepaymentScheduleResponse['data'],
        totalInterest: totalInterest as number,
        totalPrinciple: totalPrinciple as number,
        totalPayable: totalPayable as number,
        overrideScheduleDates
    };
};

type PropsFromRedux = { account: any } & ReturnType<typeof mapStateToProps>;

const FormatRepaymentDate = ({
    repayment,
    index,
    overrideDateFormat
}: {
    repayment: any;
    index: number;
    overrideDateFormat?: (date: string, index: number) => string;
}) => {
    if (overrideDateFormat)
        return <>{overrideDateFormat(repayment.date, index)}</>;

    return (
        <FormattedDate
            day="numeric"
            month="short"
            year="numeric"
            value={new Date(repayment.date)}
        />
    );
};

export const RepaymentsSchedule = connect(mapStateToProps)(({
    schedule,
    totalInterest,
    totalPrinciple,
    totalPayable,
    overrideScheduleDates
}: PropsFromRedux) => {
    const intl = useIntl();
    const [showAll, setShowAll] = useState(false);

    return (
        <>
            <Table
                data-testid="repaymentScheduleTable"
                columns={[
                    {
                        id: 'date',
                        header: intl.formatMessage(messages.dateTitle),
                        cell: ({ row }) => (
                            <FormatRepaymentDate
                                overrideDateFormat={overrideScheduleDates}
                                repayment={row.original}
                                index={row.index}
                            />
                        ),
                        footer: intl.formatMessage(messages.totalTitle)
                    },
                    {
                        id: 'principle',
                        header: intl.formatMessage(messages.principleTitle),
                        cell: ({ row }) => (
                            <FormattedCurrency value={row.original.principal} />
                        ),
                        footer: () => (
                            <FormattedCurrency value={totalPrinciple} />
                        )
                    },
                    {
                        id: 'interest',
                        header: intl.formatMessage(messages.interestTitle),
                        cell: ({ row }) => (
                            <FormattedCurrency value={row.original.interest} />
                        ),
                        footer: () => (
                            <FormattedCurrency value={totalInterest} />
                        )
                    },
                    {
                        id: 'total',
                        header: intl.formatMessage(messages.totalTitle),
                        cell: ({ row }) => (
                            <FormattedCurrency value={row.original.total} />
                        ),
                        footer: () => <FormattedCurrency value={totalPayable} />
                    }
                ]}
                data={showAll ? schedule : schedule.slice(0, 3)}
            />
            {schedule.length > 3 && (
                <Button
                    variant="tertiary"
                    size="small"
                    onClick={() => setShowAll(!showAll)}
                    className={styles.showMore}
                >
                    {showAll ? (
                        <span>Show less</span>
                    ) : (
                        <span>View full schedule</span>
                    )}
                    {showAll ? <IconMinus /> : <IconPlus />}
                </Button>
            )}
        </>
    );
});
